/**
 * ProfileSync — bridge between gameStore (in-memory player state) and Supabase.
 *
 * Post-multi-character (migration 009):
 *   profiles  = account-level: id, premium, active_character_id
 *   characters = per-character: id, user_id, character_index, name,
 *                character_class, level, xp, credits, current_hp,
 *                last_tick_at, meta, created_at, updated_at
 *   inventory_stacks / equipment_instances / auto_miners / active_boosts
 *     are now FK'd on `character_id` (not `player_id`).
 *
 * Reads:   `loadAccount(userId)` → AccountProfile + character list (lightweight).
 *          `loadCharacter(characterId)` → full Player (the one the user picked).
 * Writes:  `startSync(characterId, getPlayer)` starts a debounced ~2 s flush
 *          of the active character's per-table state.
 *
 * Account-level mutations (`setActiveCharacter`, `createCharacter`,
 * `deleteCharacter`, `updatePremium`) bypass the sync loop because they
 * happen at character-switch / character-create boundaries, not during play.
 */
import { supabase } from '@/lib/supabase';
import type {
  ActiveBoost,
  BonusStat,
  CharacterClass,
  CharacterSummary,
  DecrypterState,
  EquipmentInstance,
  EquipmentSlot,
  EquipmentStats,
  EquippedMap,
  GlitchPerk,
  InventoryStack,
  Player,
} from '@/domain/types';
import { calcEquippedItemPower } from '@/domain/equipment';
import { isUuid, migrateToV2, randomId } from '@/domain/player';
import { GAME_VERSION } from '@/domain/constants';
import { flushQueue as flushTelemetryQueue } from '@/state/telemetry';

const SYNC_INTERVAL_MS = 2000;
// Bumped 20 → 28 with the Vault-Knight set expansion (3 new slots, ~3× more
// equipment drops landing in stash). Without the bump players cap out within
// a single tier-4+ run.
const STARTER_INVENTORY_SLOTS = 28;

// Slot cap mirrored from the SQL CHECK on characters.character_index.
export const MAX_CHARACTER_SLOTS = 3;

// ── Types ─────────────────────────────────────────────────────

interface RemoteProfileRow {
  id: string;
  premium: number;
  active_character_id: string | null;
  created_at: string;
  updated_at: string;
}

interface RemoteCharacterRow {
  id: string;
  user_id: string;
  character_index: number;
  name: string;
  character_class: CharacterClass;
  level: number;
  xp: number;
  credits: number;
  current_hp: number;
  last_tick_at: string;
  meta: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface RemoteEquipmentRow {
  id: string;
  character_id: string;
  item_id: string;
  upgrade_level: number;
  equipped_slot: EquipmentSlot | null;
  meta: Record<string, unknown> | null;
  created_at: string;
}

interface RemoteInventoryRow {
  character_id: string;
  item_id: string;
  quantity: number;
}

interface RemoteMinerRow {
  character_id: string;
  miner_id: string;
  count: number;
}

interface RemoteBoostRow {
  id: string;
  character_id: string;
  boost_id: string;
  expires_at: string;
}

export interface AccountProfile {
  userId: string;
  premium: number;
  activeCharacterId: string | null;
  characters: CharacterSummary[];
}

// ── Account-level reads ───────────────────────────────────────

/** Loads the account row + the user's character list. Returns null if the
 *  profile doesn't exist yet (first login). The character list is empty
 *  for fresh accounts — caller routes the user to character creation. */
export async function loadAccount(userId: string): Promise<AccountProfile | null> {
  const [profileRes, charsRes] = await Promise.all([
    supabase.from('profiles').select('id, premium, active_character_id, created_at, updated_at').eq('id', userId).maybeSingle(),
    supabase.from('characters')
      .select('id, user_id, character_index, name, character_class, level, last_tick_at')
      .eq('user_id', userId)
      .order('character_index', { ascending: true }),
  ]);

  if (profileRes.error) throw profileRes.error;
  if (!profileRes.data) return null;
  if (charsRes.error) throw charsRes.error;

  const profile = profileRes.data as RemoteProfileRow;
  const charRows = (charsRes.data as Array<Pick<RemoteCharacterRow,
    'id' | 'user_id' | 'character_index' | 'name' | 'character_class' | 'level' | 'last_tick_at'>>) ?? [];

  return {
    userId: profile.id,
    premium: profile.premium,
    activeCharacterId: profile.active_character_id,
    characters: charRows.map((r) => ({
      id: r.id,
      userId: r.user_id,
      characterIndex: r.character_index,
      characterClass: r.character_class,
      name: r.name,
      level: r.level,
      lastTickAt: new Date(r.last_tick_at).getTime(),
    })),
  };
}

/** Fresh account row — called once after sign-up. Doesn't create any
 *  character; the caller takes the user to character creation next. */
export async function createAccount(userId: string): Promise<void> {
  const { error } = await supabase.from('profiles').insert({
    id: userId,
    premium: 0,
    active_character_id: null,
  });
  if (error) throw error;
}

export async function setActiveCharacter(userId: string, characterId: string): Promise<void> {
  const { error } = await supabase.from('profiles')
    .update({ active_character_id: characterId })
    .eq('id', userId);
  if (error) throw error;
}

export async function updatePremium(userId: string, premium: number): Promise<void> {
  const { error } = await supabase.from('profiles').update({ premium }).eq('id', userId);
  if (error) throw error;
}

// ── Character-level: list / load / create / delete ────────────

/** Light list-only fetch — same data as `loadAccount().characters`. Use
 *  when you need a fresh list without re-pulling the account row. */
export async function listCharacters(userId: string): Promise<CharacterSummary[]> {
  const { data, error } = await supabase.from('characters')
    .select('id, user_id, character_index, name, character_class, level, last_tick_at')
    .eq('user_id', userId)
    .order('character_index', { ascending: true });
  if (error) throw error;
  return ((data as Array<Pick<RemoteCharacterRow,
    'id' | 'user_id' | 'character_index' | 'name' | 'character_class' | 'level' | 'last_tick_at'>>) ?? [])
    .map((r) => ({
      id: r.id,
      userId: r.user_id,
      characterIndex: r.character_index,
      characterClass: r.character_class,
      name: r.name,
      level: r.level,
      lastTickAt: new Date(r.last_tick_at).getTime(),
    }));
}

/** Pull the full Player record for the given character. Returns null if
 *  the character was deleted between list-fetch and select-click. */
export async function loadCharacter(characterId: string): Promise<Player | null> {
  const [charRes, invRes, eqRes, minerRes, boostRes] = await Promise.all([
    supabase.from('characters').select('*').eq('id', characterId).maybeSingle(),
    supabase.from('inventory_stacks').select('*').eq('character_id', characterId),
    supabase.from('equipment_instances').select('*').eq('character_id', characterId),
    supabase.from('auto_miners').select('*').eq('character_id', characterId),
    supabase.from('active_boosts').select('*').eq('character_id', characterId),
  ]);

  if (charRes.error) throw charRes.error;
  if (!charRes.data) return null;
  if (invRes.error) throw invRes.error;
  if (eqRes.error) throw eqRes.error;
  if (minerRes.error) throw minerRes.error;
  if (boostRes.error) throw boostRes.error;

  const character = charRes.data as RemoteCharacterRow;

  const equipment: EquippedMap = {};
  const equipmentStash: EquipmentInstance[] = [];
  for (const row of (eqRes.data as RemoteEquipmentRow[]) ?? []) {
    const rowMeta = row.meta as Record<string, unknown> | null;
    const inst: EquipmentInstance = {
      id: row.id,
      itemId: row.item_id,
      upgradeLevel: row.upgrade_level,
      rolledStats: rowMeta?.rolledStats as EquipmentStats | undefined,
      bonusStats: rowMeta?.bonusStats as BonusStat[] | undefined,
      ascended: rowMeta?.ascended as boolean | undefined,
      ascensionTier: rowMeta?.ascensionTier as number | undefined,
      glitchPerks: rowMeta?.glitchPerks as GlitchPerk[] | undefined,
    };
    if (row.equipped_slot) equipment[row.equipped_slot] = inst;
    else equipmentStash.push(inst);
  }

  const slots: InventoryStack[] = ((invRes.data as RemoteInventoryRow[]) ?? []).map((r) => ({
    itemId: r.item_id,
    quantity: r.quantity,
  }));

  const autoMiners: Record<string, number> = {};
  for (const row of (minerRes.data as RemoteMinerRow[]) ?? []) {
    autoMiners[row.miner_id] = row.count;
  }

  const boosts: ActiveBoost[] = ((boostRes.data as RemoteBoostRow[]) ?? []).map((r) => ({
    boostId: r.boost_id,
    expiresAt: new Date(r.expires_at).getTime(),
  }));

  const meta = (character.meta ?? {}) as Record<string, unknown>;

  const loaded: Player = {
    id: character.id,
    userId: character.user_id,
    characterIndex: character.character_index,
    characterClass: character.character_class,
    name: character.name,
    level: character.level,
    xp: character.xp,
    gameVersion: (meta.gameVersion as number) ?? 1,
    credits: character.credits,
    premium: 0, // account-level — fetched separately via loadAccount; kept 0 here so old code that reads player.premium doesn't blow up
    cleanCode: (meta.cleanCode as number) ?? 0,
    corruptedData: (meta.corruptedData as number) ?? 0,
    neonGems: (meta.neonGems as number) ?? 0,
    heat: (meta.heat as number) ?? 0,
    overheatedUntil: (meta.overheatedUntil as number) ?? 0,
    inventory: { slots, maxSlots: STARTER_INVENTORY_SLOTS },
    equipment,
    equipmentStash,
    autoMiners,
    decrypters: (meta.decrypters as Record<string, DecrypterState>) ?? {},
    boosts,
    currentHp: character.current_hp,
    lastTickAt: new Date(character.last_tick_at).getTime(),
    createdAt: new Date(character.created_at).getTime(),
    updatedAt: new Date(character.updated_at).getTime(),
    highestVaultTier: (meta.highestVaultTier as number) ?? 0,
    skillSlots: (meta.skillSlots as [import('@/domain/types').SkillId | null, import('@/domain/types').SkillId | null]) ?? [null, null],
    unlockedSkills: (meta.unlockedSkills as import('@/domain/types').SkillId[]) ?? [],
    marketManipEndsAt: (meta.marketManipEndsAt as number) ?? 0,
    autoFarmerVaultId: (meta.autoFarmerVaultId as string | null) ?? null,
    lastAutoFarmAt: (meta.lastAutoFarmAt as number) ?? 0,
    avatarColor: (meta.avatarColor as string) ?? 'blue',
    research: (meta.research as Record<string, number>) ?? {},
    skillWebNodes: (meta.skillWebNodes as Record<string, number>) ?? {},
    lastBossKillAt: (meta.lastBossKillAt as Record<string, number>) ?? {},
    ascensionFragments: (meta.ascensionFragments as number) ?? 0,
    legendaryPity: (meta.legendaryPity as number) ?? 0,
    bestEndlessWave: (meta.bestEndlessWave as number) ?? 0,
    skillWebRespecCount: (meta.skillWebRespecCount as number) ?? 0,
    unlockedCosmetics: (meta.unlockedCosmetics as Record<string, { unlockedAt: number; equipped: boolean }>) ?? {},
    achievements: (meta.achievements as Record<string, { unlockedAt?: number; progress: number; claimedAt?: number }>) ?? {},
    dailyQuest: (meta.dailyQuest as Player['dailyQuest']) ?? null,
    tutorialStep: (meta.tutorialStep as number) ?? 0,
    augments: (meta.augments as Player['augments']) ?? { survivor: 0, aggressor: 0, hunter: 0, engineer: 0, mystic: 0 },
    aspects: (meta.aspects as Player['aspects']) ?? {
      primary: 'bulwark',
      secondary: null,
      levels: { bulwark: 1, pyromant: 0, stormcaller: 0, vampire: 0, phantom: 0, engineer: 0 },
      unlocked: ['bulwark'],
    },
    unlockedActiveSkills: (meta.unlockedActiveSkills as Player['unlockedActiveSkills']) ?? ['frost_nova'],
    activeSkillId: (meta.activeSkillId as Player['activeSkillId']) ?? 'frost_nova',
  };

  return migrateToV2(loaded);
}

/** Insert a new character row + all its empty sub-tables, then return the
 *  generated UUID. Caller is responsible for follow-up `setActiveCharacter`
 *  if this should become the active selection. */
export async function createCharacter(
  userId: string,
  characterIndex: number,
  characterClass: CharacterClass,
  player: Player,
): Promise<string> {
  // Let Postgres pick the UUID — we don't want client-side collisions.
  const insertRow = {
    user_id: userId,
    character_index: characterIndex,
    name: player.name,
    character_class: characterClass,
    level: player.level,
    xp: player.xp,
    credits: player.credits,
    current_hp: player.currentHp,
    last_tick_at: new Date(player.lastTickAt).toISOString(),
    meta: buildCharacterMeta(player),
  };

  const { data, error } = await supabase.from('characters')
    .insert(insertRow)
    .select('id')
    .single();
  if (error) throw error;
  const newId = (data as { id: string }).id;

  // Push the sub-tables (inventory, equipment, miners, boosts) as the new
  // character's initial state. We DON'T use the player.id here because the
  // server just generated a fresh UUID for the character.
  await pushFullState(newId, player);
  return newId;
}

/** Delete a character + its cascade-FK'd inventory/equipment/miners/boosts.
 *  If this was the account's active character, the caller is responsible
 *  for picking a new one (or routing back to the select screen). */
export async function deleteCharacter(characterId: string): Promise<void> {
  const { error } = await supabase.from('characters').delete().eq('id', characterId);
  if (error) throw error;
}

// ── Back-compat shims ─────────────────────────────────────────
// These map the old single-character API onto the new multi-character
// helpers. They're entry-points for callers that haven't been migrated
// to the explicit `loadAccount` + `loadCharacter` flow yet
// (App.tsx initial gate, marketStore post-trade refresh). Phase C.4 will
// replace them with explicit `selectCharacter` routing.

/** Loads the user's currently-active character (or the first character
 *  if no active is set). Returns null if the account has no characters
 *  yet — caller should then route to character-create. */
export async function loadProfile(userId: string): Promise<Player | null> {
  const acct = await loadAccount(userId);
  if (!acct) return null;
  let cid = acct.activeCharacterId;
  if (!cid && acct.characters.length > 0) cid = acct.characters[0].id;
  if (!cid) return null;
  return loadCharacter(cid);
}

/** Creates an account row plus the first character (slot 0). Returns
 *  void to match the legacy signature; the caller doesn't currently need
 *  the new character_id, but Phase C.4 will refactor to use the explicit
 *  helpers and pass the id back. */
export async function createProfile(userId: string, player: Player): Promise<void> {
  await createAccount(userId);
  const charClass: CharacterClass = player.characterClass ?? 'hollow_knight';
  const newCharacterId = await createCharacter(userId, 0, charClass, player);
  await setActiveCharacter(userId, newCharacterId);
  // Update the in-memory player so subsequent sync ticks point at the
  // server-generated UUID instead of the temporary client id.
  player.id = newCharacterId;
  player.userId = userId;
  player.characterIndex = 0;
  player.characterClass = charClass;
}

// ── Internal: meta builder + push helpers (per-character) ─────

function buildCharacterMeta(player: Player): Record<string, unknown> {
  return {
    gameVersion:       player.gameVersion ?? GAME_VERSION,
    cleanCode:         player.cleanCode ?? 0,
    corruptedData:     player.corruptedData ?? 0,
    neonGems:          player.neonGems ?? 0,
    heat:              player.heat ?? 0,
    overheatedUntil:   player.overheatedUntil ?? 0,
    decrypters:        player.decrypters ?? {},
    highestVaultTier:  player.highestVaultTier ?? 0,
    skillSlots:        player.skillSlots ?? [null, null],
    unlockedSkills:    player.unlockedSkills ?? [],
    marketManipEndsAt: player.marketManipEndsAt ?? 0,
    autoFarmerVaultId: player.autoFarmerVaultId ?? null,
    lastAutoFarmAt:    player.lastAutoFarmAt ?? 0,
    itemPower:         calcEquippedItemPower(player.equipment),
    avatarColor:       player.avatarColor ?? 'blue',
    research:          player.research ?? {},
    skillWebNodes:     player.skillWebNodes ?? {},
    lastBossKillAt:    player.lastBossKillAt ?? {},
    ascensionFragments: player.ascensionFragments ?? 0,
    legendaryPity:     player.legendaryPity ?? 0,
    bestEndlessWave:   player.bestEndlessWave ?? 0,
    skillWebRespecCount: player.skillWebRespecCount ?? 0,
    unlockedCosmetics:  player.unlockedCosmetics ?? {},
    achievements:       player.achievements ?? {},
    dailyQuest:         player.dailyQuest ?? null,
    tutorialStep:       player.tutorialStep ?? 0,
    augments:           player.augments ?? { survivor: 0, aggressor: 0, hunter: 0, engineer: 0, mystic: 0 },
    aspects:            player.aspects ?? null,
    unlockedActiveSkills: player.unlockedActiveSkills ?? [],
    activeSkillId:      player.activeSkillId ?? null,
  };
}

function logErr(label: string, err: unknown): void {
  if (err) console.error(`[profileSync] ${label} failed`, err);
}

async function pushInventory(cid: string, player: Player): Promise<void> {
  const { error: delErr } = await supabase.from('inventory_stacks').delete().eq('character_id', cid);
  logErr('inventory_stacks delete', delErr);
  if (player.inventory.slots.length > 0) {
    const { error } = await supabase.from('inventory_stacks').insert(
      player.inventory.slots.map((s) => ({
        character_id: cid,
        item_id: s.itemId,
        quantity: s.quantity,
      })),
    );
    logErr('inventory_stacks insert', error);
  }
}

async function pushEquipment(cid: string, player: Player): Promise<void> {
  // Defensive: sanitize any non-UUID equipment ids in-place BEFORE the
  // delete-then-insert dance. Postgres `equipment_instances.id` is a uuid
  // column — non-UUID ids would silently fail the upsert AFTER the delete
  // already ran, wiping the row. Past bugs: seedStarterKit and the dev
  // Spawn-Set buttons emitted `eq_xxxxx` ids; even though those are fixed
  // now, an older save might still have stale rows in Zustand persist.
  for (const slot of Object.keys(player.equipment) as Array<keyof typeof player.equipment>) {
    const inst = player.equipment[slot];
    if (inst && !isUuid(inst.id)) {
      player.equipment[slot] = { ...inst, id: randomId() };
    }
  }
  player.equipmentStash = player.equipmentStash.map((inst) =>
    isUuid(inst.id) ? inst : { ...inst, id: randomId() }
  );

  // Listing-FK protection: market listings reference equipment_instances.id.
  // A naive delete-then-insert would CASCADE the listing row away.
  // Listings.seller_id still references profiles(id) = account.userId, NOT
  // characters, so we look up by the account-owner's id.
  const userId = player.userId ?? null;
  const protectedIds = new Set<string>();
  if (userId) {
    const { data: openListings, error: listErr } = await supabase
      .from('listings')
      .select('instance_id')
      .eq('seller_id', userId)
      .eq('status', 'open');
    logErr('listings fetch (sync-protect)', listErr);
    for (const r of (openListings as Array<{ instance_id: string }> | null) ?? []) {
      protectedIds.add(r.instance_id);
    }
  }

  let deleteQuery = supabase.from('equipment_instances').delete().eq('character_id', cid);
  if (protectedIds.size > 0) {
    const inList = `(${[...protectedIds].join(',')})`;
    deleteQuery = deleteQuery.not('id', 'in', inList);
  }
  const { error: delErr } = await deleteQuery;
  logErr('equipment_instances delete', delErr);

  const allEquipment: Array<{
    id: string; character_id: string; item_id: string; upgrade_level: number;
    equipped_slot: EquipmentSlot | null; meta: Record<string, unknown>;
  }> = [];
  for (const [slot, inst] of Object.entries(player.equipment)) {
    if (!inst) continue;
    if (protectedIds.has(inst.id)) continue;
    allEquipment.push({
      id: inst.id, character_id: cid, item_id: inst.itemId,
      upgrade_level: inst.upgradeLevel, equipped_slot: slot as EquipmentSlot,
      meta: {
        rolledStats: inst.rolledStats, bonusStats: inst.bonusStats,
        ascended: inst.ascended, ascensionTier: inst.ascensionTier, glitchPerks: inst.glitchPerks,
      },
    });
  }
  for (const inst of player.equipmentStash) {
    if (protectedIds.has(inst.id)) continue;
    allEquipment.push({
      id: inst.id, character_id: cid, item_id: inst.itemId,
      upgrade_level: inst.upgradeLevel, equipped_slot: null,
      meta: {
        rolledStats: inst.rolledStats, bonusStats: inst.bonusStats,
        ascended: inst.ascended, ascensionTier: inst.ascensionTier, glitchPerks: inst.glitchPerks,
      },
    });
  }
  if (allEquipment.length > 0) {
    const { error } = await supabase.from('equipment_instances').upsert(allEquipment, { onConflict: 'id' });
    logErr('equipment_instances upsert', error);
  }
}

async function pushMiners(cid: string, player: Player): Promise<void> {
  const { error: delErr } = await supabase.from('auto_miners').delete().eq('character_id', cid);
  logErr('auto_miners delete', delErr);
  const minerRows = Object.entries(player.autoMiners)
    .filter(([, count]) => count > 0)
    .map(([miner_id, count]) => ({ character_id: cid, miner_id, count }));
  if (minerRows.length > 0) {
    const { error } = await supabase.from('auto_miners').upsert(minerRows, { onConflict: 'character_id,miner_id' });
    logErr('auto_miners upsert', error);
  }
}

async function pushBoosts(cid: string, player: Player): Promise<void> {
  const { error: delErr } = await supabase.from('active_boosts').delete().eq('character_id', cid);
  logErr('active_boosts delete', delErr);
  const boostRows = player.boosts
    .filter((b) => b.expiresAt > Date.now())
    .map((b) => ({
      character_id: cid,
      boost_id: b.boostId,
      expires_at: new Date(b.expiresAt).toISOString(),
    }));
  if (boostRows.length > 0) {
    const { error } = await supabase.from('active_boosts').upsert(boostRows, { onConflict: 'character_id,boost_id' });
    logErr('active_boosts upsert', error);
  }
}

async function pushCharacter(cid: string, player: Player): Promise<void> {
  const { error } = await supabase.from('characters').update({
    name: player.name,
    level: player.level,
    xp: player.xp,
    credits: player.credits,
    current_hp: player.currentHp,
    last_tick_at: new Date(player.lastTickAt).toISOString(),
    meta: buildCharacterMeta(player),
  }).eq('id', cid);
  if (error) throw error;
}

/** Replace ALL of a character's tables with the in-memory state. Use
 *  sparingly (migration / brand-new character creation). */
export async function pushFullState(cid: string, player: Player): Promise<void> {
  await pushInventory(cid, player);
  await pushEquipment(cid, player);
  await pushMiners(cid, player);
  await pushBoosts(cid, player);
}

// ── Debounced sync ────────────────────────────────────────────

type SyncTable = 'character' | 'inventory' | 'equipment' | 'miners' | 'boosts';

let dirty = false;
let activeCharacterId: string | null = null;
let getPlayer: (() => Player | null) | null = null;
let intervalHandle: number | null = null;
const lastHashes: Partial<Record<SyncTable, string>> = {};

/** Start the per-character flush loop. Pass the CHARACTER's UUID (not the
 *  account's user_id). Call `stopSync()` before switching characters so
 *  the next sync flushes against the right row. */
export function startSync(characterId: string, getPlayerFn: () => Player | null): void {
  activeCharacterId = characterId;
  getPlayer = getPlayerFn;
  dirty = false;
  for (const k of Object.keys(lastHashes) as SyncTable[]) delete lastHashes[k];

  if (intervalHandle !== null) clearInterval(intervalHandle);
  intervalHandle = window.setInterval(() => {
    void flushIfDirty();
  }, SYNC_INTERVAL_MS);
}

export function stopSync(): void {
  if (intervalHandle !== null) {
    clearInterval(intervalHandle);
    intervalHandle = null;
  }
  activeCharacterId = null;
  getPlayer = null;
  dirty = false;
  for (const k of Object.keys(lastHashes) as SyncTable[]) delete lastHashes[k];
}

export function markDirty(): void {
  dirty = true;
}

function hashCharacter(player: Player): string {
  return JSON.stringify({
    n: player.name, l: player.level, x: player.xp, c: player.credits,
    h: player.currentHp, t: player.lastTickAt,
    gv: player.gameVersion ?? GAME_VERSION,
    cc: player.cleanCode ?? 0, cd: player.corruptedData ?? 0, ng: player.neonGems ?? 0,
    he: player.heat ?? 0, ou: player.overheatedUntil ?? 0,
    dc: player.decrypters ?? {}, hv: player.highestVaultTier ?? 0,
    ss: player.skillSlots ?? [null, null], us: player.unlockedSkills ?? [],
    mm: player.marketManipEndsAt ?? 0,
    af: player.autoFarmerVaultId ?? null, la: player.lastAutoFarmAt ?? 0,
    ip: calcEquippedItemPower(player.equipment),
    ac: player.avatarColor ?? 'blue',
    re: player.research ?? {}, sw: player.skillWebNodes ?? {},
    bk: player.lastBossKillAt ?? {},
    af2: player.ascensionFragments ?? 0, lp: player.legendaryPity ?? 0,
    bew: player.bestEndlessWave ?? 0,
    swr: player.skillWebRespecCount ?? 0,
    uc:  player.unlockedCosmetics ?? {},
    ach: player.achievements ?? {},
    dq:  player.dailyQuest ?? null,
    ts:  player.tutorialStep ?? 0,
    aug: player.augments ?? null,
    asp: player.aspects ?? null,
    uas: player.unlockedActiveSkills ?? [],
    ask: player.activeSkillId ?? null,
  });
}

function hashInventory(player: Player): string {
  return JSON.stringify(player.inventory.slots);
}

function hashEquipment(player: Player): string {
  return JSON.stringify({ eq: player.equipment, st: player.equipmentStash });
}

function hashMiners(player: Player): string {
  return JSON.stringify(player.autoMiners);
}

function hashBoosts(player: Player): string {
  return JSON.stringify(
    player.boosts.filter((b) => b.expiresAt > Date.now()).map((b) => [b.boostId, b.expiresAt]),
  );
}

let isFlushing = false;

async function flushIfDirty(): Promise<void> {
  if (!dirty || !activeCharacterId || !getPlayer) return;
  if (syncLocked) return;
  if (isFlushing) return;
  const player = getPlayer();
  if (!player) return;

  dirty = false;
  isFlushing = true;
  try {
    const newHashes: Record<SyncTable, string> = {
      character: hashCharacter(player),
      inventory: hashInventory(player),
      equipment: hashEquipment(player),
      miners:    hashMiners(player),
      boosts:    hashBoosts(player),
    };

    if (newHashes.character !== lastHashes.character) {
      await pushCharacter(activeCharacterId, player);
      lastHashes.character = newHashes.character;
    }
    if (newHashes.inventory !== lastHashes.inventory) {
      await pushInventory(activeCharacterId, player);
      lastHashes.inventory = newHashes.inventory;
    }
    if (newHashes.equipment !== lastHashes.equipment) {
      await pushEquipment(activeCharacterId, player);
      lastHashes.equipment = newHashes.equipment;
    }
    if (newHashes.miners !== lastHashes.miners) {
      await pushMiners(activeCharacterId, player);
      lastHashes.miners = newHashes.miners;
    }
    if (newHashes.boosts !== lastHashes.boosts) {
      await pushBoosts(activeCharacterId, player);
      lastHashes.boosts = newHashes.boosts;
    }
    // Opportunistic: drain queued aspect-telemetry events on every profile
    // flush. Best-effort + idempotent — if it fails, the queue stays
    // intact for the next tick.
    void flushTelemetryQueue();
  } catch (err) {
    console.error('[profileSync] flush failed', err);
    dirty = true;
  } finally {
    isFlushing = false;
  }
}

export async function flushNow(): Promise<void> {
  await flushIfDirty();
}

// ── Sync-Lock ─────────────────────────────────────────────────

let syncLocked = false;

export function lockSync(): void {
  syncLocked = true;
}

export function unlockSync(): void {
  syncLocked = false;
}

export function isSyncLocked(): boolean {
  return syncLocked;
}
